import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import ArtistLayout from "@/components/layout/ArtistLayout";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronDown, ChevronUp, RefreshCw, Check, Image,
  ExternalLink, Send, AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ArtistRecovery = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [submitDialog, setSubmitDialog] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/artist/recovery/photo-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroups(response.data);
    } catch (error) {
      toast.error("Failed to load photo groups");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const claimGroup = async (folderId) => {
    setClaiming(folderId);
    try {
      await axios.post(
        `${API}/artist/recovery/claim`,
        { folder_id: folderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Photos claimed! You can now fill in your submission details.");
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim photos");
    } finally {
      setClaiming(null);
    }
  };

  const openSubmitDialog = (group) => {
    setSubmitDialog(group);
    setForm({
      folder_id: group.folder_id,
      artist_name: user?.name || user?.alias_name || "",
      city: "",
      venue: "",
      date: group.files[0]?.created_at?.slice(0, 10) || "",
      s_user_id: user?.s_user_id || "",
      merchandise_url: "",
      proof_of_play_url: "",
      bottle_console_url: "",
      branding_stories_post_url: "",
      instagram_analytics_url: "",
      instagram_analytics_impressions: 0,
      track_play_count: 0,
    });
  };

  const handleSubmit = async () => {
    if (!form.artist_name) {
      toast.error("Please enter your artist name");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      for (const key of ["merchandise_url", "proof_of_play_url", "bottle_console_url", "branding_stories_post_url", "instagram_analytics_url"]) {
        if (!payload[key]) payload[key] = null;
      }
      await axios.post(`${API}/artist/recovery/submit`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Submission recovered! The agency will review it.");
      setSubmitDialog(null);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const mediaFields = [
    { key: "merchandise_url", label: "Merchandise Photo" },
    { key: "proof_of_play_url", label: "Proof of Play" },
    { key: "bottle_console_url", label: "Bottle Console" },
    { key: "branding_stories_post_url", label: "Branding / Stories Post" },
    { key: "instagram_analytics_url", label: "Instagram Analytics Screenshot" },
  ];

  const myGroups = groups.filter((g) => g.status === "mine");
  const unclaimedGroups = groups.filter((g) => g.status === "unclaimed");
  const otherGroups = groups.filter((g) => g.status === "claimed");

  if (loading) {
    return (
      <ArtistLayout>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="animate-spin text-[#00B4E5]" size={32} />
          <span className="ml-3 text-gray-400">Loading recovery data...</span>
        </div>
      </ArtistLayout>
    );
  }

  return (
    <ArtistLayout>
      <div className="max-w-5xl mx-auto" data-testid="artist-recovery">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-white mb-2">RECOVER YOUR SUBMISSIONS</h1>
          <p className="text-gray-400">
            We found old campaign photos in our storage. Browse the groups below, identify your photos,
            claim them, and re-submit your details.
          </p>
        </div>

        {/* My Claimed Groups */}
        {myGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-lg text-[#00B4E5] mb-3 uppercase tracking-wider">Your Claimed Photos</h2>
            <div className="space-y-3">
              {myGroups.map((group) => (
                <GroupCard
                  key={group.folder_id}
                  group={group}
                  expanded={expandedGroup === group.folder_id}
                  onToggle={() => setExpandedGroup(expandedGroup === group.folder_id ? null : group.folder_id)}
                  onSubmit={() => openSubmitDialog(group)}
                  variant="mine"
                />
              ))}
            </div>
          </div>
        )}

        {/* Unclaimed Groups */}
        {unclaimedGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-lg text-amber-400 mb-3 uppercase tracking-wider">
              Unclaimed Photos ({unclaimedGroups.length} groups)
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Look through the photos below. If you recognize yours, click "These Are Mine" to claim them.
            </p>
            <div className="space-y-3">
              {unclaimedGroups.map((group) => (
                <GroupCard
                  key={group.folder_id}
                  group={group}
                  expanded={expandedGroup === group.folder_id}
                  onToggle={() => setExpandedGroup(expandedGroup === group.folder_id ? null : group.folder_id)}
                  onClaim={() => claimGroup(group.folder_id)}
                  claiming={claiming === group.folder_id}
                  variant="unclaimed"
                />
              ))}
            </div>
          </div>
        )}

        {/* Already Claimed by Others */}
        {otherGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-lg text-gray-500 mb-3 uppercase tracking-wider">
              Claimed by Other Artists ({otherGroups.length})
            </h2>
            <div className="space-y-3">
              {otherGroups.map((group) => (
                <GroupCard
                  key={group.folder_id}
                  group={group}
                  expanded={expandedGroup === group.folder_id}
                  onToggle={() => setExpandedGroup(expandedGroup === group.folder_id ? null : group.folder_id)}
                  variant="claimed"
                />
              ))}
            </div>
          </div>
        )}

        {groups.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 p-12 text-center">
            <Check className="mx-auto text-green-400 mb-3" size={40} />
            <p className="text-white font-heading text-xl">ALL CAUGHT UP</p>
            <p className="text-gray-400 mt-2">No photos to recover. All submissions are up to date.</p>
          </div>
        )}

        {/* Submit Dialog */}
        <Dialog open={!!submitDialog} onOpenChange={() => setSubmitDialog(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">RE-SUBMIT YOUR DETAILS</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded flex items-start gap-2">
                <AlertTriangle size={16} className="text-blue-500 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700">
                  Your photos are safe. Fill in the details below and assign each photo to the correct field.
                  Click a thumbnail to copy its URL, then paste into the field.
                </p>
              </div>

              {/* Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Event Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Artist / DJ Name *</Label>
                    <Input
                      value={form.artist_name || ""}
                      onChange={(e) => setForm((f) => ({ ...f, artist_name: e.target.value }))}
                      data-testid="recovery-artist-name"
                    />
                  </div>
                  <div>
                    <Label>S-USER ID</Label>
                    <Input
                      value={form.s_user_id || ""}
                      onChange={(e) => setForm((f) => ({ ...f, s_user_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={form.city || ""}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Venue</Label>
                    <Input
                      value={form.venue || ""}
                      onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Event Date</Label>
                    <Input
                      type="date"
                      value={form.date || ""}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Track Play Count</Label>
                    <Input
                      type="number"
                      value={form.track_play_count || 0}
                      onChange={(e) => setForm((f) => ({ ...f, track_play_count: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
              </div>

              {/* Media Assignment */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Assign Your Photos</h3>
                {mediaFields.map((field) => (
                  <div key={field.key}>
                    <Label className="flex items-center gap-2 text-sm">
                      <Image size={14} /> {field.label}
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={form[field.key] || ""}
                        onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                        placeholder="Click a photo below to copy its URL, then paste here"
                        className="text-xs"
                      />
                      {form[field.key] && (
                        <a href={form[field.key]} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm" type="button">
                            <ExternalLink size={14} />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}

                {/* Thumbnails */}
                <div className="border border-dashed border-gray-300 rounded p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-2">Click a photo to copy its URL:</p>
                  <div className="grid grid-cols-5 gap-2">
                    {submitDialog?.files?.map((file, idx) => (
                      <div
                        key={idx}
                        className="cursor-pointer border-2 border-transparent rounded overflow-hidden hover:border-[#00B4E5] transition"
                        onClick={() => {
                          navigator.clipboard.writeText(file.url);
                          toast.success("URL copied! Paste into a field above.");
                        }}
                      >
                        <img src={file.url} alt="" className="w-full h-20 object-cover" loading="lazy" />
                        <p className="text-[9px] text-center text-gray-400 py-0.5">{file.created_at?.slice(0, 10)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instagram impressions */}
                <div>
                  <Label>Total Instagram Impressions</Label>
                  <Input
                    type="number"
                    value={form.instagram_analytics_impressions || 0}
                    onChange={(e) => setForm((f) => ({ ...f, instagram_analytics_impressions: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setSubmitDialog(null)}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !form.artist_name}
                className="bg-[#00B4E5] hover:bg-[#0096c4] text-white"
                data-testid="recovery-submit-btn"
              >
                {submitting ? "Submitting..." : <><Send size={16} className="mr-2" /> Submit Recovery</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ArtistLayout>
  );
};

const GroupCard = ({ group, expanded, onToggle, onClaim, onSubmit, claiming, variant }) => {
  const borderColor = variant === "mine" ? "border-[#00B4E5]/30" : variant === "unclaimed" ? "border-amber-500/20" : "border-gray-800";
  
  return (
    <div className={`bg-gray-900 border ${borderColor} overflow-hidden`} data-testid={`group-${group.folder_id.slice(0, 8)}`}>
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50 transition"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          {/* Preview thumbnails */}
          <div className="flex -space-x-2">
            {group.files.slice(0, 3).map((f, i) => (
              <img
                key={i}
                src={f.url}
                alt=""
                className="w-10 h-10 rounded object-cover border-2 border-gray-900"
                loading="lazy"
              />
            ))}
            {group.file_count > 3 && (
              <div className="w-10 h-10 rounded bg-gray-800 border-2 border-gray-900 flex items-center justify-center text-xs text-gray-400">
                +{group.file_count - 3}
              </div>
            )}
          </div>
          <div>
            <p className="text-white font-semibold">
              {group.file_count} photos
              {variant === "mine" && <span className="text-[#00B4E5] text-xs ml-2">CLAIMED BY YOU</span>}
              {variant === "claimed" && <span className="text-gray-500 text-xs ml-2">CLAIMED</span>}
            </p>
            <p className="text-sm text-gray-500">Uploaded: {group.date_range}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {variant === "unclaimed" && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onClaim(); }}
              disabled={claiming}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid={`claim-${group.folder_id.slice(0, 8)}`}
            >
              {claiming ? "Claiming..." : "These Are Mine"}
            </Button>
          )}
          {variant === "mine" && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onSubmit(); }}
              className="bg-[#00B4E5] hover:bg-[#0096c4] text-white font-semibold"
              data-testid={`submit-${group.folder_id.slice(0, 8)}`}
            >
              <Send size={14} className="mr-1" /> Fill In Details
            </Button>
          )}
          {expanded ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-800 p-4">
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
            {group.files.map((file, idx) => (
              <a key={idx} href={file.url} target="_blank" rel="noreferrer" className="group">
                <div className="relative border border-gray-800 rounded overflow-hidden hover:border-[#00B4E5] transition">
                  <img src={file.url} alt="" className="w-full h-24 object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                    <ExternalLink size={16} className="text-white" />
                  </div>
                  <p className="text-[9px] text-center text-gray-500 py-0.5">{file.created_at?.slice(0, 10)}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistRecovery;
